import GiftCodesCard from "../components/GiftCodesCard";
import React, { useMemo } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useApiResource } from "../api/useApi";
import { listUploads, getImproveOverview } from "../api/resources";
import { uploadsToSessions } from "../api/adapters";
import { NEXT_FEATURES } from "../featureFlags";
import {
  ME,
  SESSIONS,
  Session,
  Athlete,
  ATHLETES,
  ACTIVITY_LABEL,
  EXPLORE_FEED,
  TRENDS,
  GLOBAL_ORG_ID,
  MY_PRS,
  MY_GOALS,
  Org,
  formatTime,
  formatPlays,
  riskClass,
  RiskLevel,
} from "../mockData";
import { StatCard } from "../components/StatCard";
import { StatusBadge } from "../components/StatusBadge";
import { RiskBadge } from "../components/RiskBadge";
import { TrendCard, FeedCard } from "./Explore";
import {
  ArrowRight,
  Flame,
  Trophy,
  Target,
  Play,
  TrendingUp,
  Users,
} from "lucide-react";

// KPI tile titles emitted by /platform/uploadImproveOverview (PlatformController override).
const KPI_TITLES = {
  AVG: "Avg quality",
  BEST: "Best score",
  STREAK: "Streak",
  PHASES: "Active phases",
} as const;

type FeedRow =
  | { kind: "session"; data: Session; mine: boolean; person?: Athlete }
  | { kind: "trend";   data: typeof TRENDS[number] }
  | { kind: "pick";    data: typeof EXPLORE_FEED[number] };

/** The hero's sentence, from the account's real numbers only (owner, 2026-10-13: "fix the home screen to use real
 * data" — it used to be a fixed paragraph about quality "up across the board" and a "+5°" lead-arm gain). */
export function homeSummary(latestScore: number | null, avgQuality: number, bestScore: number): string {
  if (latestScore == null && avgQuality <= 0) return "Upload a swing to get your first score.";
  const parts: string[] = [];
  if (latestScore != null) parts.push(`Your latest capture scored ${latestScore}.`);
  if (avgQuality > 0) parts.push(`Your average over the last 12 weeks is ${avgQuality}`);
  if (bestScore > 0) parts.push(avgQuality > 0 ? `and your best is ${bestScore}.` : `Your best is ${bestScore}.`);
  else if (avgQuality > 0) parts[parts.length - 1] += ".";
  return parts.join(" ");
}

/** The name the greeting uses: the first name, never a raw email. Auth0 fills `name` with the email for a password
 * sign-up (owner, 2026-10-21: "Hi olly.dolan@gmail.com"), so an email-shaped name is cut to the part before @, then to
 * its first word ("olly.dolan" → "Olly"). */
export function greetingName(user: { given_name?: string; name?: string; email?: string } | undefined, fallback = ""): string {
  const raw = (user?.given_name || user?.name || user?.email || fallback).toString().trim();
  const local = raw.includes("@") ? raw.split("@")[0] : raw;
  const first = local.split(/[\s._+-]+/).find((w) => w.length > 0) ?? "";
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : fallback;
}

export default function Home() {
  const { org } = useOutletContext<{ org: Org }>();
  const { user } = useAuth0();

  // Real: my uploads from /platform/uploads1, adapted to the Session shape. The home dashboard only
  // shows recent activity, so request the first (most-recent) page rather than the whole history —
  // this keeps the request fast on accounts with many uploads.
  const { data: uploadsResp, isLoading: uploadsLoading, error: uploadsError, reload: reloadUploads } = useApiResource(
    (apiFetch) => listUploads(apiFetch, { page: 1, pageSize: 10 }),
    [],
  );
  const mine = useMemo<Session[]>(
    () =>
      uploadsToSessions(uploadsResp?.values, {
        athleteName: user?.name ?? user?.email ?? ME.name,
        athleteId: user?.sub ?? ME.athleteId,
      }),
    [uploadsResp, user],
  );
  const latest = mine[0];
  // The real total (the list request only loads the newest 10; counting those capped every account at 10).
  const captureCount = uploadsResp?.totalCount ?? mine.length;
  // 5 most recent uploads for the "Your recent sessions" block underneath the hero.
  const recent = useMemo(() => mine.slice(0, 5), [mine]);

  // Real KPI tiles from /platform/uploadImproveOverview (PlatformController override).
  // Falls through to zeros if the user has no uploads yet.
  const { data: improveOverview, isLoading: statsLoading, error: statsError, reload: reloadStats } = useApiResource(
    (apiFetch) => getImproveOverview(apiFetch),
    [],
  );
  // Waiting, or failed, is said as such (owner, 2026-11-01: "the home screen doesn't have any loaders, so you don't know
  // if the data is live or waiting to be loaded"): until a request answers, its numbers are placeholders, never zeros.
  const uploadsPending = uploadsResp == null && uploadsLoading;
  const uploadsFailed = uploadsResp == null && !uploadsLoading && uploadsError != null;
  const statsPending = improveOverview == null && statsLoading;
  const statsFailed = improveOverview == null && !statsLoading && statsError != null;
  const kpis = useMemo(() => {
    const tiles = improveOverview?.kpiTiles ?? [];
    const find = (title: string) =>
      tiles.find((t) => t.title === title)?.numberValue ?? 0;
    return {
      avgQuality: Number(find(KPI_TITLES.AVG)),
      bestScore: Number(find(KPI_TITLES.BEST)),
      streakWeeks: Number(find(KPI_TITLES.STREAK)),
      activePhases: Number(find(KPI_TITLES.PHASES)),
    };
  }, [improveOverview]);

  // Network: people in this org / global pool, not me.
  const networkSessions = useMemo(() => {
    const pool =
      org.id === GLOBAL_ORG_ID
        ? ATHLETES.filter((a) => a.id !== ME.athleteId)
        : ATHLETES.filter((a) => a.orgId === org.id && a.id !== ME.athleteId);
    const ids = new Set(pool.map((a) => a.id));
    return SESSIONS.filter((s) => ids.has(s.athleteId)).slice(0, 4);
  }, [org.id]);

  // Top trends by global plays.
  const topTrends = useMemo(
    () => [...TRENDS].sort((a, b) => b.plays - a.plays).slice(0, 2),
    [],
  );
  // Bigger Trending list for the side rail.
  const trendingList = useMemo(
    () => [...TRENDS].sort((a, b) => b.plays - a.plays).slice(0, 5),
    [],
  );
  // Suggested follows: people in this org / global pool, not me.
  const suggested = useMemo(() => {
    const pool =
      org.id === GLOBAL_ORG_ID
        ? ATHLETES.filter((a) => a.id !== ME.athleteId)
        : ATHLETES.filter((a) => a.orgId === org.id && a.id !== ME.athleteId);
    return pool.slice(0, 4);
  }, [org.id]);

  // Curated picks (highest-scoring public analyses).
  const curated = useMemo(
    () => [...EXPLORE_FEED].sort((a, b) => b.score - a.score).slice(0, 4),
    [],
  );

  // Org risk surface — only when relevant in this org.
  const orgRiskSessions = useMemo(
    () => SESSIONS.filter((s) => s.orgId === org.id && s.risk),
    [org.id],
  );
  const showRiskWidget = orgRiskSessions.length > 0;

  // Mixed feed in the bottom half: my newest → trend → network → curated → trend → older mine.
  const feed = useMemo<FeedRow[]>(() => {
    const personFor = (s: Session) =>
      ATHLETES.find((a) => a.id === s.athleteId);
    const rows: FeedRow[] = [];

    if (mine[1]) rows.push({ kind: "session", data: mine[1], mine: true });
    if (topTrends[0]) rows.push({ kind: "trend", data: topTrends[0] });
    networkSessions.slice(0, 2).forEach((s) =>
      rows.push({ kind: "session", data: s, mine: false, person: personFor(s) }),
    );
    curated.slice(0, 2).forEach((p) => rows.push({ kind: "pick", data: p }));
    if (topTrends[1]) rows.push({ kind: "trend", data: topTrends[1] });
    mine.slice(2, 4).forEach((s) =>
      rows.push({ kind: "session", data: s, mine: true }),
    );
    curated.slice(2).forEach((p) => rows.push({ kind: "pick", data: p }));
    networkSessions.slice(2).forEach((s) =>
      rows.push({ kind: "session", data: s, mine: false, person: personFor(s) }),
    );

    return rows;
  }, [mine, networkSessions, topTrends, curated]);

  // A brand-new user (their uploads loaded, none yet) gets ONE first step and what happens after it (owner, 2026-10-18
  // onboarding plan: Home said both "open the mobile app to record your first" and "upload your first video").
  const firstRun = uploadsResp != null && captureCount === 0;

  return (
    <div className="space-y-4">
      {firstRun && (
        <div className="card p-6" data-testid="home-first-run">
          <h2 className="text-lg font-semibold text-slate-900">Get your first swing analysed</h2>
          <ol className="mt-3 space-y-1.5 text-sm text-slate-600 list-decimal list-inside">
            <li>Choose a clip of your swing (a longer one is trimmed in the page).</li>
            <li>It is analysed on your device: your pose, frame by frame.</li>
            <li>In a minute or two: your key positions, cues and coaching, read out to you.</li>
          </ol>
          <Link to="/upload" className="btn-primary mt-4 inline-flex !h-10 px-5">
            Upload a swing
          </Link>
        </div>
      )}
      {/* Hero — greeting, the AI take and the latest capture as a line, one card (owner, 2026-10-21: "we dont need that
          video view here, the stuff on the left is more relevant"). */}
      <div className="card p-5 flex flex-col" data-testid="home-hero" aria-busy={uploadsPending || statsPending}>
        <div className="flex items-center gap-2 flex-wrap">
          {kpis.streakWeeks > 0 && (
            <span className="badge bg-amber-50 text-amber-700 ring-1 ring-amber-200">
              <Flame className="h-3 w-3" /> {kpis.streakWeeks}-week streak
            </span>
          )}
          {uploadsPending ? (
            <span className="h-5 w-20 rounded-full bg-slate-100 animate-pulse" />
          ) : !uploadsFailed && (
            <span className="badge bg-el-50 text-el-700 ring-1 ring-el-200">
              {captureCount} {captureCount === 1 ? "capture" : "captures"}
            </span>
          )}
        </div>
        <h2 className="mt-3 text-xl font-semibold tracking-tightish text-slate-900">
          Hi {greetingName(user, ME.name)} — every session, every angle. Auto-analysed.
        </h2>
        {uploadsPending || statsPending ? (
          <div className="mt-3 space-y-2" data-testid="home-summary-loading">
            <div className="h-3 w-3/4 rounded bg-slate-100 animate-pulse" />
            <div className="h-3 w-1/2 rounded bg-slate-100 animate-pulse" />
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-700 leading-relaxed" data-testid="home-summary">
            {homeSummary(latest?.score ?? null, kpis.avgQuality, kpis.bestScore)}
          </p>
        )}
        {uploadsPending && <div className="mt-4 h-11 rounded-md bg-slate-50 animate-pulse" />}
        {latest && (
          <Link
            to={`/sessions/${latest.id}`}
            className="mt-4 flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2 hover:border-el-300 hover:bg-el-50/40 transition group"
            data-testid="home-latest-capture"
          >
            <span className="text-xl leading-none">{latest.thumb}</span>
            <div className="min-w-0 flex-1 text-sm">
              <span className="font-medium text-slate-900">{ACTIVITY_LABEL[latest.activity]}</span>
              <span className="text-slate-500"> · {formatTime(latest.capturedAt)}</span>
            </div>
            <div className="shrink-0 text-sm text-slate-900" data-testid="home-latest-quality">
              {latest.score != null ? (
                <>
                  Quality {latest.score}
                  {kpis.bestScore > 0 && latest.score >= kpis.bestScore && (
                    <>
                      {" "}
                      <span className="text-el-700 text-xs font-semibold">PR</span>
                    </>
                  )}
                </>
              ) : (
                "Not scored yet"
              )}
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-el-600 group-hover:translate-x-0.5 transition" />
          </Link>
        )}
        <div className="pt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <Link to="/progress" className="text-el-700 hover:text-el-800 inline-flex items-center gap-1 font-medium">
            See trend <ArrowRight className="h-3 w-3" />
          </Link>
          <Link to="/upload" className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 font-medium">
            Capture new <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* KPIs — fed from /platform/uploadImproveOverview (real upload history). Not on a first run: all zeros. */}
      {!firstRun && (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Captures"     value={uploadsFailed ? "—" : captureCount} loading={uploadsPending} unit={captureCount === 1 ? "capture" : "captures"} intent="positive" />
        <StatCard label="Avg quality"  value={kpis.avgQuality > 0 ? kpis.avgQuality : "—"} loading={statsPending} unit="/100 · 12 weeks" intent="positive" />
        <StatCard label="Best score"   value={kpis.bestScore > 0 ? kpis.bestScore : "—"}  loading={statsPending} unit="score"   intent="positive" />
        <StatCard label="Streak"       value={statsFailed ? "—" : `${kpis.streakWeeks}w`} loading={statsPending} unit="weeks"  intent="positive" />
      </div>
      )}
      {statsFailed && (
        <div className="text-xs text-slate-500" data-testid="home-stats-error">
          Couldn't load your stats.{" "}
          <button type="button" onClick={reloadStats} className="font-medium text-el-700 hover:text-el-800">Retry</button>
        </div>
      )}

      {/* Recent sessions — real uploads, drop straight into SessionDetail. */}
      <RecentSessions sessions={recent} loading={uploadsPending} failed={uploadsFailed} onRetry={reloadUploads} />

      {/* Org risk overview — appears whenever this org has risk-tagged captures.
          Mock-only today; gate until /platform reports real risk data. */}
      {NEXT_FEATURES && showRiskWidget && (
        <OrgRiskOverview org={org} sessions={orgRiskSessions} />
      )}

      {/* Social feed + side rail — all mock today. Hidden until each block has
          a real backend source. Toggle via featureFlags.NEXT_FEATURES. */}
      {NEXT_FEATURES && (
      <div className="flex flex-col xl:flex-row gap-4">
        {/* Feed — fixed narrow column */}
        <div className="w-full xl:w-[576px] xl:shrink-0 space-y-4 mx-auto xl:mx-0">
          <div className="flex items-center justify-between px-1">
            <div>
              <div className="text-sm font-semibold text-slate-900">For you</div>
              <div className="text-xs text-slate-500">
                Your sessions, what your network is doing, plus what's trending.
              </div>
            </div>
            <Link to="/sessions" className="text-xs font-medium text-el-700 hover:text-el-800">
              All sessions →
            </Link>
          </div>

          <div className="space-y-4">
            {feed.map((row, i) => {
              if (row.kind === "session") {
                return (
                  <SessionPost
                    key={`s-${row.data.id}-${i}`}
                    session={row.data}
                    mine={row.mine}
                    person={row.person}
                  />
                );
              }
              if (row.kind === "trend") {
                return <TrendCard key={`t-${row.data.id}-${i}`} trend={row.data} />;
              }
              return <FeedCard key={`p-${row.data.id}-${i}`} item={row.data} />;
            })}
          </div>
        </div>

        {/* Side rail — flex-grows into the space the narrow feed leaves behind */}
        <aside className="flex-1 min-w-0 space-y-4">
          {/* PRs */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="h-4 w-4 text-el-600" />
              <div className="text-sm font-semibold text-slate-900">Personal records</div>
            </div>
            <ul className="space-y-2.5">
              {MY_PRS.slice(0, 3).map((pr) => (
                <li key={pr.id} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-900 font-medium truncate">{pr.metric}</div>
                    <div className="text-[0.7rem] text-slate-500 truncate">{pr.context}</div>
                  </div>
                  <div className="text-sm font-semibold text-el-700 shrink-0">{pr.value}</div>
                </li>
              ))}
            </ul>
          </div>

          {/* Goals */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-el-600" />
              <div className="text-sm font-semibold text-slate-900">Goals</div>
            </div>
            <ul className="space-y-3">
              {MY_GOALS.map((g) => (
                <li key={g.id}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-800 truncate pr-2">{g.label}</span>
                    <span className="text-xs text-slate-500 shrink-0">{g.progress}%</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-el-400 to-el-600"
                      style={{ width: `${g.progress}%` }}
                    />
                  </div>
                  <div className="text-[0.65rem] text-slate-400 mt-0.5">{g.target} · due {g.due}</div>
                </li>
              ))}
            </ul>
          </div>

          {/* Trending now */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <TrendingUp className="h-4 w-4 text-el-600 shrink-0" />
                <div className="text-sm font-semibold text-slate-900 truncate">Trending now</div>
              </div>
              <Link to="/explore/trends" className="text-xs text-el-700 hover:text-el-800">
                All →
              </Link>
            </div>
            <ul className="space-y-2.5">
              {trendingList.map((t, i) => (
                <li key={t.id}>
                  <Link
                    to={`/explore/trend/${t.id}`}
                    className="flex items-center gap-2.5 text-sm hover:text-el-800"
                  >
                    <span className="w-5 text-center text-xs font-semibold tabular-nums text-slate-400 shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-lg leading-none">{t.emoji}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-900 font-medium truncate">{t.title}</div>
                      <div className="text-[0.7rem] text-slate-500 truncate">
                        {t.song} · {t.artist}
                      </div>
                    </div>
                    <span className="text-xs text-slate-500 tabular-nums shrink-0">
                      {formatPlays(t.plays)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Suggested for you — people you can follow / connect with */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Users className="h-4 w-4 text-el-600 shrink-0" />
                <div className="text-sm font-semibold text-slate-900 truncate">Suggested for you</div>
              </div>
              <Link to="/roster" className="text-xs text-el-700 hover:text-el-800">
                Roster →
              </Link>
            </div>
            <ul className="space-y-2.5">
              {suggested.map((p) => (
                <li key={p.id} className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-el-200 to-el-400 text-white text-[0.65rem] font-semibold flex items-center justify-center shrink-0">
                    {p.initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/roster/${p.id}`}
                      className="block text-sm font-medium text-slate-900 hover:text-el-800 truncate"
                    >
                      {p.name}
                    </Link>
                    <div className="text-[0.7rem] text-slate-500 truncate">
                      {p.activity}{p.team ? ` · ${p.team}` : ""}
                    </div>
                  </div>
                  <button className="btn-ghost !h-7 !text-[0.7rem] shrink-0">
                    Follow
                  </button>
                </li>
              ))}
            </ul>
          </div>

        </aside>
      </div>
      )}
      <GiftCodesCard />
    </div>
  );
}

/* ---------- Recent sessions — real uploads ---------- */

function RecentSessions({ sessions, loading = false, failed = false, onRetry }: {
  sessions: Session[]; loading?: boolean; failed?: boolean; onRetry?: () => void;
}) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">Your recent sessions</div>
          <div className="text-xs text-slate-500">
            Your most recent captures, fresh from the analysis pipeline.
          </div>
        </div>
        <Link to="/sessions" className="text-xs font-medium text-el-700 hover:text-el-800">
          All sessions →
        </Link>
      </div>
      {loading ? (
        <ul className="space-y-1.5" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="h-[52px] rounded-md bg-slate-50 animate-pulse" data-testid="home-recent-loading" />
          ))}
        </ul>
      ) : failed ? (
        <div className="text-sm text-slate-500 py-4 text-center" data-testid="home-recent-error">
          Couldn't load your sessions.{" "}
          <button type="button" onClick={onRetry} className="font-medium text-el-700 hover:text-el-800">Retry</button>
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center">
          No captures yet —{" "}
          <Link to="/upload" className="text-el-700 hover:text-el-800 font-medium">
            upload your first video
          </Link>
          .
        </div>
      ) : (
        <ul className="space-y-1.5">
          {sessions.map((s) => (
            <li key={s.id}>
              <Link
                to={`/sessions/${s.id}`}
                className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-slate-50 transition"
              >
                <div className="h-9 w-9 rounded-md bg-slate-900/90 text-white text-lg flex items-center justify-center shrink-0">
                  {s.thumb}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {ACTIVITY_LABEL[s.activity]}
                  </div>
                  <div className="text-[0.7rem] text-slate-500 truncate">
                    {formatTime(s.capturedAt)}
                    {s.status === "processed" && (s.score ?? 0) > 0 && (
                      <> · Quality {s.score}/100</>
                    )}
                  </div>
                </div>
                <StatusBadge status={s.status} />
                <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Session post (feed-style row) ---------- */

function SessionPost({
  session,
  mine,
  person,
}: {
  session: Session;
  mine: boolean;
  person?: Athlete;
}) {
  const name = mine ? "You" : person?.name ?? "Someone";
  const initials = mine ? "YOU" : person?.initials ?? "??";

  return (
    <Link
      to={`/sessions/${session.id}`}
      className="card p-0 overflow-hidden block hover:ring-1 hover:ring-el-200 transition group"
    >
      <div className="px-4 pt-3 pb-2 flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-el-200 to-el-400 text-white text-[0.65rem] font-semibold flex items-center justify-center shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900 truncate inline-flex items-center gap-1.5">
            {name}
            {!mine && <Users className="h-3 w-3 text-slate-400" />}
          </div>
          <div className="text-[0.7rem] text-slate-500 truncate">
            {ACTIVITY_LABEL[session.activity]} · {formatTime(session.capturedAt)}
            {!mine && person?.team && (
              <span className="text-slate-400"> · {person.team}</span>
            )}
          </div>
        </div>
        <StatusBadge status={session.status} />
      </div>

      <div className="aspect-video bg-slate-900/90 relative flex items-center justify-center text-5xl">
        <span className="opacity-90">{session.thumb}</span>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <button className="absolute h-12 w-12 rounded-full bg-white/95 group-hover:bg-white flex items-center justify-center shadow-lg">
          <Play className="h-5 w-5 text-el-700" />
        </button>
      </div>

      {session.status === "processed" && (
        <div className="p-4 flex items-center justify-between">
          <div className="text-sm">
            <div className="text-slate-900">
              Quality{" "}
              <span className="font-semibold">{session.score}</span>
              <span className="text-slate-500">/100</span>
            </div>
            <div className="text-[0.7rem] text-slate-500">
              Spine {session.metrics.spineAngle.toFixed(1)}° · Hip{" "}
              {session.metrics.hipRotation.toFixed(1)}° · Shoulder{" "}
              {session.metrics.shoulderRotation.toFixed(1)}°
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-el-600 group-hover:translate-x-0.5 transition" />
        </div>
      )}
    </Link>
  );
}

/* ---------- Org risk overview (kept from original) ---------- */

function OrgRiskOverview({ org, sessions }: { org: Org; sessions: Session[] }) {
  const dist = sessions.reduce<Record<RiskLevel, number>>(
    (acc, s) => {
      if (s.risk) acc[s.risk] = (acc[s.risk] ?? 0) + 1;
      return acc;
    },
    { green: 0, yellow: 0, red: 0 } as Record<RiskLevel, number>,
  );
  const total = dist.green + dist.yellow + dist.red || 1;
  const high = sessions.filter((s) => s.risk === "red").slice(0, 3);

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            Risk overview · {org.name}
          </div>
          <div className="text-xs text-slate-500">
            From task assessments captured in this organisation
          </div>
        </div>
        <Link to="/reports" className="text-xs font-medium text-el-700 hover:text-el-800">
          Risk reports →
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <RiskTile risk="red" label="High risk" count={dist.red} total={total} />
        <RiskTile risk="yellow" label="Moderate risk" count={dist.yellow} total={total} />
        <RiskTile risk="green" label="Low risk" count={dist.green} total={total} />
      </div>

      {high.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <div className="text-[0.7rem] uppercase tracking-[0.06em] font-semibold text-slate-500 mb-2">
            Action queue · {high.length} high-risk
          </div>
          <ul className="space-y-2">
            {high.map((s) => (
              <li
                key={s.id}
                className="rounded-md bg-rose-50/60 ring-1 ring-rose-100 px-3 py-2 flex items-center gap-3"
              >
                <RiskBadge risk={s.risk!} />
                <div className="flex-1 min-w-0 text-xs">
                  <div className="font-medium text-slate-900 truncate">
                    {s.task ?? ACTIVITY_LABEL[s.activity]} · {s.athlete}
                  </div>
                  {s.notes && (
                    <div className="text-slate-600 truncate">{s.notes}</div>
                  )}
                </div>
                <Link to={`/sessions/${s.id}`} className="btn-secondary !h-7 !text-[0.7rem] shrink-0">
                  Open
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RiskTile({
  risk, label, count, total,
}: { risk: RiskLevel; label: string; count: number; total: number }) {
  const c = riskClass(risk);
  const pct = (count / total) * 100;
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full ${c.dot}`} />
        <div className="text-[0.7rem] uppercase tracking-[0.06em] font-semibold text-slate-500">{label}</div>
      </div>
      <div className={`mt-1 text-2xl leading-none font-semibold tracking-tightish ${c.text}`}>
        {count}
      </div>
      <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${c.dot}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

